import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { getDatabase, off, onValue, ref } from 'firebase/database';
import store from 'app/store';
import { startSession, stopSession, updateSession } from 'common/slice/sessionListSlice';
import { Session } from 'globals/interfaces';
import { getParticipantGroupSessionsPath } from 'helpers/firebasePaths';

const SESSION_UPDATE_DEBOUNCE_MS = 250;

const sessionListener = createListenerMiddleware();
const streamingDeviceListeners = new Map<string, () => void>();
const sessionGroupListeners = new Map<number, () => void>();
const setupStreamingDeviceListener = (db: ReturnType<typeof getDatabase>, session: Session) => {
  const { session_id } = session;

  const disabledRef = ref(
    db,
    `session/${session_id}/streaming_device/is_secondary_disabled`
  );
  if (streamingDeviceListeners.has(session_id)) {
    off(disabledRef);
    streamingDeviceListeners.delete(session_id);
  }
  onValue(disabledRef, (disabledSnapshot) => {
    const newValue = disabledSnapshot.val();
    store.dispatch((dispatch, getState) => {
      const currentSessions: Session[] = getState().sessionList.sessions;
      const currentSession = currentSessions.find((s) => s.session_id === session_id);

      if (currentSession?.is_secondary_disabled !== newValue) {
        const updatedSessions = currentSessions.map((s) =>
          s.session_id === session_id ? { ...s, is_secondary_disabled: newValue } : s
        );
        dispatch(updateSession(updatedSessions));
      }
    });
  });

  streamingDeviceListeners.set(session_id, () => off(disabledRef));
};

sessionListener.startListening({
  matcher: isAnyOf(startSession),
  effect: async (action) => {
    if (!startSession.match(action)) return;

    const participantGroupId = action.payload;
    if (participantGroupId === undefined || participantGroupId === null) return;
    const db = getDatabase();

    const existingListener = sessionGroupListeners.get(participantGroupId);
    if (existingListener) {
      existingListener();
      sessionGroupListeners.delete(participantGroupId);
    }

    const groupSessionsRef = ref(db, getParticipantGroupSessionsPath(participantGroupId));

    let pendingPayload: Session[] | null = null;
    let debounceHandle: ReturnType<typeof setTimeout> | null = null;
    const flushUpdates = () => {
      if (pendingPayload === null) {
        debounceHandle = null;
        return;
      }
      store.dispatch(updateSession(pendingPayload));
      if (pendingPayload.length) {
        pendingPayload.forEach((session) => {
          if (session.configuration?.secondary_camera) {
            setupStreamingDeviceListener(db, session);
          }
        });
      }
      pendingPayload = null;
      debounceHandle = null;
    };

    const unsubscribe = onValue(groupSessionsRef, (snapshot) => {
      const data = snapshot.val();
      pendingPayload = data
        ? Object.keys(data).map((key) => ({
            ...data[key],
            session_id: key,
          }))
        : [];

      if (!debounceHandle) {
        debounceHandle = setTimeout(flushUpdates, SESSION_UPDATE_DEBOUNCE_MS);
      }
    });

    sessionGroupListeners.set(participantGroupId, () => {
      if (debounceHandle) {
        clearTimeout(debounceHandle);
        flushUpdates();
      }
      unsubscribe();
    });
  },
});

sessionListener.startListening({
  matcher: isAnyOf(stopSession),
  effect: async (action) => {
    if (!stopSession.match(action)) return;
    const participantGroupId = action.payload;
    const unsubscribe = sessionGroupListeners.get(participantGroupId);
    if (unsubscribe) {
      unsubscribe();
      sessionGroupListeners.delete(participantGroupId);
    }
    streamingDeviceListeners.forEach((cleanup) => cleanup());
    streamingDeviceListeners.clear();
  },
});

export default sessionListener;
